import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, Trash2, Lock, Users } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import Dialog from '../ui/Dialog';
import Field from '../ui/Field';
import { ServerError, SubmitButton } from './formBits';
import MissingFinancialsNotice, { missingFinancialsEngagementId, missingFinancialsProjectId } from '../engagement/missingFinancials';
import {
  buildBulkBody, countBulkAssignees, countBulkTasks, emptyGroup, emptySub, emptyTask, serverBulkErrors, validateBulk,
  type BulkErrors, type BulkGroup, type BulkSub, type BulkTask,
} from './bulkAddModel';
import type { Milestone } from '../../types';
import EntityPicker from '../ui/EntityPicker';
import DateInput from '../ui/DateInput';

/**
 * P-05/F (D-43, D-52) — "הוספת אבני דרך ומשימות", rebuilt:
 *
 *   ▾ אבן דרך: [ממשק ניהול]  יעד [  ]
 *       משימה: [שם *] [משויכים] [הערכת שעות *] [יעד]  🗑
 *       ▾ תת-אבן דרך: [הרשאות]  יעד [  ]
 *           משימה: [...]   + משימה
 *       + משימה    + תת-אבן דרך
 *   ▾ אבן דרך קיימת: [🔒 QA ▾]
 *       + משימה    + תת-אבן דרך (חדשה או קיימת)
 *   + אבן דרך חדשה    + משימות לאבן דרך קיימת    + משימות ללא אבן דרך
 *                                                 [ביטול] [שמירת הכול]
 *
 * A task has ONLY: name *, assignees, estimate (optional, D-328), due date (D-43). "שמירת
 * הכול" saves the whole tree in ONE server transaction (POST
 * /projects/:id/bulk-add): any error → nothing is saved and the wrong fields
 * are marked; missing financial data → the existing D-33 message.
 */
export default function BulkAddModal({ projectId, projectName, organizationId, milestones, onClose }: {
  projectId: string;
  projectName: string;
  organizationId: string;
  milestones: Milestone[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [groups, setGroups] = useState<BulkGroup[]>(() => [emptyGroup('new')]);
  const [errors, setErrors] = useState<BulkErrors>({});
  const [serverError, setServerError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  // D-75 — ONE "לשייך גם?" for the whole group, asked on "שמירת הכול".
  const [askParents, setAskParents] = useState<number | null>(null);

  const { data: agentsRes } = useQuery({
    queryKey: ['assignable-agents', organizationId],
    queryFn: () => api.getAssignableAgents(organizationId),
    staleTime: 60_000,
    enabled: !!organizationId,
  });
  const agents = agentsRes?.data ?? [];

  const live = useMemo(() => milestones.filter((m) => !m.archived_at), [milestones]);
  const topLevel = useMemo(() => live.filter((m) => !m.parent_milestone_id), [live]);
  const subsOf = (id: string) => live.filter((m) => m.parent_milestone_id === id);

  const updateGroup = (key: string, fn: (g: BulkGroup) => BulkGroup) => setGroups((gs) => gs.map((g) => (g.key === key ? fn(g) : g)));
  const updateSub = (gKey: string, sKey: string, fn: (s: BulkSub) => BulkSub) =>
    updateGroup(gKey, (g) => ({ ...g, subs: g.subs.map((s) => (s.key === sKey ? fn(s) : s)) }));
  const clearError = (k: string) => setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });

  const save = async (alsoAssignParents?: boolean) => {
    setServerError(null);
    const found = validateBulk(groups, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const first = document.querySelector<HTMLElement>('[data-bulk-error="true"]');
      first?.focus();
      return;
    }
    // D-75 — workers were assigned: one question for all of them, then save.
    const workers = countBulkAssignees(groups);
    if (workers > 0 && alsoAssignParents === undefined) {
      setAskParents(workers);
      return;
    }
    setAskParents(null);
    const { body, pathToKey } = buildBulkBody(groups);
    setSaving(true);
    try {
      const res = await api.bulkAddMilestonesAndTasks(projectId, { ...body, also_assign_parents: alsoAssignParents === true });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-tickets', projectId] });
      queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
      toast('success', t('bulkAdd.saved', { milestones: res.data?.milestones.filter((m) => m.created).length ?? 0, tasks: res.data?.tasks.length ?? 0 }));
      onClose();
    } catch (err: any) {
      const list = err?.response?.data?.error?.details?.errors;
      if (Array.isArray(list) && list.length > 0) setErrors(serverBulkErrors(list, pathToKey));
      setServerError(err);
    } finally {
      setSaving(false);
    }
  };

  const nTasks = countBulkTasks(groups);
  const empty = groups.length === 0;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('bulkAdd.title', { project: projectName })}
      size="xl"
      dismissible={!saving}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>{t('common.cancel')}</button>
          <SubmitButton form="bulk-add-form" busy={saving} disabled={empty}>{t('bulkAdd.saveAll')}</SubmitButton>
        </>
      )}
    >
      <form id="bulk-add-form" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }} className="space-y-3">
        {/* QA-R2/15 (D-33) — the price / payment terms are completed in the project's edit form. */}
        {missingFinancialsEngagementId(serverError) ? (
          <MissingFinancialsNotice engagementId={missingFinancialsEngagementId(serverError)!} projectId={missingFinancialsProjectId(serverError) ?? projectId} />
        ) : (
          <ServerError error={serverError} fallback={t('bulkAdd.saveFailed')} />
        )}
        {Object.keys(errors).length > 0 && <p role="alert" className="text-sm text-danger-700">{t('bulkAdd.fixErrors')}</p>}

        {groups.map((g) => (
          <GroupBlock
            key={g.key}
            group={g}
            topLevel={topLevel}
            subsOf={subsOf}
            agents={agents}
            errors={errors}
            clearError={clearError}
            onChange={(fn) => updateGroup(g.key, fn)}
            onChangeSub={(sKey, fn) => updateSub(g.key, sKey, fn)}
            onRemove={() => setGroups((gs) => gs.filter((x) => x.key !== g.key))}
          />
        ))}

        <div className="flex flex-wrap gap-2 border-t border-border pt-3">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGroups((gs) => [...gs, emptyGroup('new')])}>
            <Plus size={14} aria-hidden="true" /> {t('bulkAdd.addMilestone')}
          </button>
          {topLevel.length > 0 && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGroups((gs) => [...gs, emptyGroup('existing')])}>
              <Plus size={14} aria-hidden="true" /> {t('bulkAdd.addToExisting')}
            </button>
          )}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setGroups((gs) => [...gs, emptyGroup('none')])}>
            <Plus size={14} aria-hidden="true" /> {t('bulkAdd.addWithoutMilestone')}
          </button>
          <span className="ms-auto self-center text-xs text-text-2">{t('bulkAdd.count', { count: nTasks })}</span>
        </div>
      </form>
      {askParents !== null && (
        <Dialog
          open
          onClose={() => setAskParents(null)}
          title={t('bulkAdd.alsoAssignTitle')}
          size="sm"
          footer={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => void save(false)}>{t('bulkAdd.alsoAssignNo')}</button>
              <button type="button" className="btn btn-primary" onClick={() => void save(true)}>{t('bulkAdd.alsoAssignYes')}</button>
            </>
          )}
        >
          <p className="text-sm text-text">{t('bulkAdd.alsoAssignQuestion', { count: askParents })}</p>
        </Dialog>
      )}
    </Dialog>
  );
}

function GroupBlock({ group: g, topLevel, subsOf, agents, errors, clearError, onChange, onChangeSub, onRemove }: {
  group: BulkGroup;
  topLevel: Milestone[];
  subsOf: (id: string) => Milestone[];
  agents: { id: string; name: string }[];
  errors: BulkErrors;
  clearError: (k: string) => void;
  onChange: (fn: (g: BulkGroup) => BulkGroup) => void;
  onChangeSub: (sKey: string, fn: (s: BulkSub) => BulkSub) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const existingSubs = g.kind === 'existing' && g.existingId ? subsOf(g.existingId) : [];
  const heading = g.kind === 'new' ? t('bulkAdd.milestone') : g.kind === 'existing' ? t('bulkAdd.existingMilestone') : t('bulkAdd.noMilestone');

  return (
    <fieldset className="rounded-lg border border-border p-3" data-testid={`bulk-group-${g.kind}`}>
      <legend className="sr-only">{heading}</legend>
      <div className="flex flex-wrap items-end gap-2">
        <button type="button" className="btn-icon text-text-3" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={heading}>
          <ChevronDown size={16} className={open ? '' : 'ltr:-rotate-90 rtl:rotate-90'} aria-hidden="true" />
        </button>
        <span className="pb-2 text-sm font-semibold text-text">{heading}</span>
        {g.kind === 'new' && (
          <>
            <TextCell label={t('bulkAdd.milestoneName')} value={g.name} error={errors[`${g.key}:name`]} required className="min-w-40 flex-1"
              onChange={(v) => { onChange((x) => ({ ...x, name: v })); clearError(`${g.key}:name`); }} />
            <DateCell label={t('bulkAdd.due')} value={g.due} onChange={(v) => onChange((x) => ({ ...x, due: v }))} />
          </>
        )}
        {g.kind === 'existing' && (
          <Field label={t('bulkAdd.pickMilestone')} error={errors[`${g.key}:existing`]} className="min-w-40 flex-1">
            <EntityPicker
              placeholder={t('bulkAdd.selectMilestone')}
              dataAttrs={{ 'data-bulk-error': errors[`${g.key}:existing`] ? 'true' : undefined }}
              options={topLevel.map((m) => ({ value: m.id, label: m.is_core ? `🔒 ${m.name}` : m.name }))}
              value={g.existingId}
              onChange={(id) => { onChange((x) => ({ ...x, existingId: id, subs: [] })); clearError(`${g.key}:existing`); }}
            />
          </Field>
        )}
        <button type="button" className="btn-icon ms-auto text-text-3 hover:text-danger-700" onClick={onRemove} aria-label={t('bulkAdd.removeGroup', { name: heading })}>
          <Trash2 size={16} aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div className="mt-2 space-y-2 ps-6">
          {g.tasks.map((task) => (
            <TaskRow key={task.key} task={task} agents={agents} errors={errors} clearError={clearError}
              onChange={(fn) => onChange((x) => ({ ...x, tasks: x.tasks.map((y) => (y.key === task.key ? fn(y) : y)) }))}
              onRemove={() => onChange((x) => ({ ...x, tasks: x.tasks.filter((y) => y.key !== task.key) }))} />
          ))}

          {g.subs.map((s) => (
            <div key={s.key} className="rounded-md border border-dashed border-border p-2" data-testid="bulk-sub">
              <div className="flex flex-wrap items-end gap-2">
                <span className="pb-2 text-sm font-medium text-text">{t('bulkAdd.subMilestone')}</span>
                {s.existingId ? (
                  <span className="pb-2 text-sm text-text-2"><Lock size={12} className="inline" aria-hidden="true" /> {existingSubs.find((m) => m.id === s.existingId)?.name ?? ''}</span>
                ) : (
                  <>
                    <TextCell label={t('bulkAdd.subMilestoneName')} value={s.name} error={errors[`${s.key}:name`]} required className="min-w-40 flex-1"
                      onChange={(v) => { onChangeSub(s.key, (x) => ({ ...x, name: v })); clearError(`${s.key}:name`); }} />
                    <DateCell label={t('bulkAdd.due')} value={s.due} onChange={(v) => onChangeSub(s.key, (x) => ({ ...x, due: v }))} />
                  </>
                )}
                <button type="button" className="btn-icon ms-auto text-text-3 hover:text-danger-700"
                  onClick={() => onChange((x) => ({ ...x, subs: x.subs.filter((y) => y.key !== s.key) }))} aria-label={t('bulkAdd.removeSub')}>
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </div>
              <div className="mt-2 space-y-2 ps-4">
                {s.tasks.map((task) => (
                  <TaskRow key={task.key} task={task} agents={agents} errors={errors} clearError={clearError}
                    onChange={(fn) => onChangeSub(s.key, (x) => ({ ...x, tasks: x.tasks.map((y) => (y.key === task.key ? fn(y) : y)) }))}
                    onRemove={() => onChangeSub(s.key, (x) => ({ ...x, tasks: x.tasks.filter((y) => y.key !== task.key) }))} />
                ))}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChangeSub(s.key, (x) => ({ ...x, tasks: [...x.tasks, emptyTask()] }))}>
                  <Plus size={14} aria-hidden="true" /> {t('bulkAdd.addTask')}
                </button>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange((x) => ({ ...x, tasks: [...x.tasks, emptyTask()] }))}>
              <Plus size={14} aria-hidden="true" /> {t('bulkAdd.addTask')}
            </button>
            {g.kind !== 'none' && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange((x) => ({ ...x, subs: [...x.subs, emptySub()] }))}>
                <Plus size={14} aria-hidden="true" /> {t('bulkAdd.addSub')}
              </button>
            )}
            {g.kind === 'existing' && existingSubs.length > 0 && (
              <EntityPicker
                className="w-56"
                aria-label={t('bulkAdd.addExistingSub')}
                placeholder={t('bulkAdd.addExistingSub')}
                options={existingSubs.filter((m) => !g.subs.some((s) => s.existingId === m.id)).map((m) => ({ value: m.id, label: m.name }))}
                value=""
                onChange={(id) => { if (id) onChange((x) => ({ ...x, subs: [...x.subs, emptySub(id)] })); }}
              />
            )}
          </div>
        </div>
      )}
    </fieldset>
  );
}

/** One task: name *, assignees, estimate *, due date — and remove (D-43: only these fields). */
function TaskRow({ task, agents, errors, clearError, onChange, onRemove }: {
  task: BulkTask;
  agents: { id: string; name: string }[];
  errors: BulkErrors;
  clearError: (k: string) => void;
  onChange: (fn: (t: BulkTask) => BulkTask) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="bulk-task">
      <TextCell label={t('bulkAdd.taskName')} value={task.title} error={errors[`${task.key}:title`]} required className="min-w-40 flex-[2]"
        onChange={(v) => { onChange((x) => ({ ...x, title: v })); clearError(`${task.key}:title`); }} />
      <AssigneesCell agents={agents} selected={task.assigneeIds} error={errors[`${task.key}:assignees`]}
        onChange={(ids) => { onChange((x) => ({ ...x, assigneeIds: ids })); clearError(`${task.key}:assignees`); }} />
      <TextCell label={t('bulkAdd.hours')} value={task.hours} error={errors[`${task.key}:hours`]} type="number" className="w-28"
        onChange={(v) => { onChange((x) => ({ ...x, hours: v })); clearError(`${task.key}:hours`); }} />
      <DateCell label={t('bulkAdd.due')} value={task.due} error={errors[`${task.key}:due`]} onChange={(v) => onChange((x) => ({ ...x, due: v }))} />
      <button type="button" className="btn-icon text-text-3 hover:text-danger-700" onClick={onRemove} aria-label={t('bulkAdd.removeTask', { name: task.title || t('bulkAdd.taskName') })}>
        <Trash2 size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

function TextCell({ label, value, onChange, error, required, type = 'text', className = '' }: {
  label: string; value: string; onChange: (v: string) => void; error?: string; required?: boolean; type?: 'text' | 'number'; className?: string;
}) {
  // P-22 (D-156 #8) - the shared Field (label + control + error), not a hand-built label.
  return (
    <Field label={label} error={error} required={required} className={className}>
      <input
        type={type}
        {...(type === 'number' ? { inputMode: 'decimal' as const, min: 0, step: '0.5' } : {})}
        className="input"
        value={value}
        data-bulk-error={error ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

function DateCell({ label, value, onChange, error }: { label: string; value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <Field label={label} error={error} className="w-36">
      <DateInput className="input" value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

/** "משויכים": a compact checklist behind a button. */
function AssigneesCell({ agents, selected, onChange, error }: {
  agents: { id: string; name: string }[]; selected: string[]; onChange: (ids: string[]) => void; error?: string;
}) {
  const { t } = useTranslation();
  const names = agents.filter((a) => selected.includes(a.id)).map((a) => a.name);
  return (
    <div className="flex w-44 flex-col gap-1 text-xs text-text-2">
      <span>{t('bulkAdd.assignees')}</span>
      <details className="relative">
        <summary className={`input flex cursor-pointer list-none items-center gap-1 truncate ${error ? 'border-danger-500' : ''}`}>
          <Users size={14} className="flex-shrink-0 text-text-3" aria-hidden="true" />
          <span className="truncate">{names.length > 0 ? names.join(', ') : t('bulkAdd.noAssignees')}</span>
        </summary>
        <div className="absolute z-popover mt-1 max-h-48 w-56 overflow-y-auto rounded-md border border-border bg-surface-raised p-2 shadow-lg">
          {agents.length === 0 && <p className="text-text-2">{t('milestoneForm.noAgents')}</p>}
          {agents.map((a) => {
            const on = selected.includes(a.id);
            return (
              <label key={a.id} className="flex min-h-8 cursor-pointer items-center gap-2 rounded px-1 text-sm text-text hover:bg-surface-subtle">
                <input type="checkbox" checked={on} onChange={() => onChange(on ? selected.filter((x) => x !== a.id) : [...selected, a.id])} />
                {a.name}
              </label>
            );
          })}
        </div>
      </details>
      {error && <span className="text-danger-700">{error}</span>}
    </div>
  );
}
